import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  useFundingInstructions,
  useMe,
  useRecipient,
  useTransfer,
} from "../../hooks/useMe";
import { publicQuoteService } from "../../services/publicQuote.service";
import { safeApiErrorMessage } from "../../utils/safeApiErrorMessage";

import { CHAT_EFFECT } from "../engine/chatStates";
import {
  conversationReducer,
  createInitialConversation,
} from "../engine/conversationReducer";
import { chatText } from "../messages";

function isQuoteExpired(quote) {
  if (!quote?.expires_at) {
    return false;
  }

  const expiresAt = new Date(
    quote.expires_at
  ).getTime();

  if (!Number.isFinite(expiresAt)) {
    return false;
  }

  return expiresAt <= Date.now();
}

function getDefaultPaymentMethod(
  recipient,
  paymentMethods
) {
  const methods =
    paymentMethods?.length
      ? paymentMethods
      : recipient?.payment_methods || [];

  return (
    methods.find(
      (method) => method.is_default
    ) ||
    methods[0] ||
    null
  );
}

export function useDeterministicChat({
  firstName = "there",
} = {}) {
  const navigate = useNavigate();

  const {
    profile,
    recipients = [],
    kycProfiles = [],

    isLoadingProfile,
    isLoadingRecipients,
    isLoadingKyc,

    createRecipientAsync,
    isCreatingRecipient,

    createTransferAsync,
    isCreatingTransfer,

    createQuoteAsync,
    acceptQuoteAsync,

    confirmTransferAsync,
    isConfirmingTransfer,

    prepareTransferAsync,
    isPreparingTransfer,
  } = useMe();

  const bottomRef = useRef(null);

  const resolvedFirstName = useMemo(
    () =>
      profile?.full_name
        ?.trim()
        ?.split(/\s+/)?.[0] ||
      firstName,
    [profile?.full_name, firstName]
  );

  const defaultCurrency =
    profile?.default_currency || "GBP";

  /*
   * Match the main Transfer page:
   * Bridge KYC specifically unlocks transfers.
   */
  const bridgeKyc = useMemo(
    () =>
      kycProfiles.find(
        (kycProfile) =>
          String(
            kycProfile?.provider || ""
          ).toLowerCase() === "bridge"
      ) || null,
    [kycProfiles]
  );

  const kycStatus =
    bridgeKyc?.status ||
    "not_started";

  const isKycApproved =
    kycStatus === "approved";

  const [state, dispatch] = useReducer(
    conversationReducer,
    {
      firstName: resolvedFirstName,
    },
    createInitialConversation
  );

  /*
   * Dynamically load the selected recipient's
   * real payment methods.
   */
  const selectedRecipientId =
    state.context?.recipient?.id || null;

  const {
    recipient: selectedRecipient,
    paymentMethods,
    isLoadingPaymentMethods,
  } = useRecipient(
    selectedRecipientId
  );

  const activeRecipient =
    selectedRecipient ||
    state.context?.recipient ||
    null;

  const defaultPaymentMethod =
    useMemo(
      () =>
        getDefaultPaymentMethod(
          activeRecipient,
          paymentMethods
        ),
      [
        activeRecipient,
        paymentMethods,
      ]
    );

  const transferId =
    state.context?.transfer?.id ||
    null;

  /*
   * These hooks give the chat the same
   * status/instruction polling used by the
   * normal transaction-detail experience.
   */
  const {
    transferDetail,
    isLoadingDetail,
  } = useTransfer(transferId, {
    pollDetail: Boolean(transferId),
  });

  const {
    fundingInstructions,
    isLoadingFundingInstructions,
    fundingInstructionsNotReady,
    fundingInstructionsError,
  } = useFundingInstructions(
    transferId,
    {
      poll:
        Boolean(transferId) &&
        state.context?.fundingMethod ===
          "bank",
    }
  );

  const sendMessage = useCallback(
    (text) => {
      dispatch({
        type: "USER_MESSAGE",
        text,
        recipients,
        defaultCurrency,
        kycStatus,
        isKycApproved,
      });
    },
    [
      recipients,
      defaultCurrency,
      kycStatus,
      isKycApproved,
    ]
  );

  /*
   * Recipient form calls this directly.
   * Recipient details do NOT have to be
   * serialized into chat messages.
   */
  const createRecipient =
    useCallback(
      async (payload) => {
        const recipient =
          await createRecipientAsync(
            payload
          );

        dispatch({
          type: "RECIPIENT_CREATED",
          recipient,
          defaultCurrency,
        });

        return recipient;
      },
      [
        createRecipientAsync,
        defaultCurrency,
      ]
    );

  const cancelRecipientCreation =
    useCallback(() => {
      dispatch({
        type:
          "RECIPIENT_CREATE_CANCELLED",
        recipients,
      });
    }, [recipients]);

  /*
   * PIN is passed directly from SecurePinCard
   * to this callback.
   *
   * It is NEVER passed through sendMessage()
   * and therefore never enters messages[].
   */
  const confirmWithPin =
    useCallback(
      async (pin) => {
        const transfer =
          state.context?.transfer;

        if (!transfer?.id) {
          return;
        }

        if (!/^\d{6}$/.test(pin)) {
          throw new Error(chatText("chat.pin.invalid"));
        }

        dispatch({
          type: "TRANSFER_CONFIRMING",
        });

        try {
          if (
            state.context.fundingMethod ===
            "card"
          ) {
            /*
             * Preserve the existing card path:
             * create transfer first, then prepare
             * with the secure transaction PIN.
             */
            await prepareTransferAsync({
              transferId: transfer.id,
              pin,
            });

            dispatch({
              type:
                "CARD_TRANSFER_PREPARED",
            });

            return;
          }

          /*
           * Bank path:
           * the create-transfer response already
           * contains an accepted quote.
           *
           * Do not re-accept it unless expired.
           */
          let authoritativeQuote =
            state.context.serverQuote ||
            transfer.quote ||
            null;

          if (
            authoritativeQuote &&
            isQuoteExpired(
              authoritativeQuote
            )
          ) {
            const refreshedQuote =
              await createQuoteAsync(
                transfer.id
              );

            await acceptQuoteAsync({
              transferId: transfer.id,
              quoteId:
                refreshedQuote.id,
            });

            authoritativeQuote =
              refreshedQuote;
          }

          await confirmTransferAsync({
            transferId: transfer.id,
            pin,
          });

          dispatch({
            type:
              "BANK_TRANSFER_CONFIRMED",
            quote: authoritativeQuote,
          });
        } catch (error) {
          dispatch({
            type:
              "TRANSFER_CONFIRM_FAILED",
            message:
              safeApiErrorMessage(
                error,
                chatText("chat.transfer.confirm_error")
              ),
          });

          throw error;
        }
      },
      [
        state.context,
        prepareTransferAsync,
        createQuoteAsync,
        acceptQuoteAsync,
        confirmTransferAsync,
      ]
    );

  /*
   * Deterministic side-effect runner.
   */
  useEffect(() => {
    const effect =
      state.effects[0];

    if (!effect) {
      return undefined;
    }

    let cancelled = false;

    async function run() {
      try {
        if (
          effect.type ===
          CHAT_EFFECT.NAVIGATE
        ) {
          navigate(effect.route);

          if (!cancelled) {
            dispatch({
              type: "CLEAR_EFFECTS",
            });
          }

          return;
        }

        if (
          effect.type ===
          CHAT_EFFECT.PREVIEW_QUOTE
        ) {
          const quote =
            await publicQuoteService.preview(
              effect.payload
            );

          if (!cancelled) {
            dispatch({
              type: "QUOTE_PREVIEWED",
              quote,
            });
          }

          return;
        }

        if (
          effect.type ===
          CHAT_EFFECT.CREATE_TRANSFER
        ) {
          /*
           * Re-check KYC immediately before
           * actual transfer creation.
           *
           * Backend remains authoritative too.
           */
          if (!isKycApproved) {
            throw new Error(chatText("chat.kyc.required_short"));
          }

          const recipient =
            activeRecipient ||
            state.context?.recipient;

          if (!recipient?.id) {
            throw new Error(chatText("chat.recipient.required"));
          }

          if (
            !defaultPaymentMethod?.id
          ) {
            throw new Error(chatText("chat.recipient.no_payment_method"));
          }

          const amount = Number(
            state.context?.amount
          );

          if (
            !Number.isFinite(amount) ||
            amount <= 0
          ) {
            throw new Error(chatText("chat.amount.required"));
          }

          const transfer =
            await createTransferAsync({
              recipient_id:
                recipient.id,

              recipient_payment_method_id:
                defaultPaymentMethod.id,

              funding_method:
                state.context
                  .fundingMethod,

              send_amount: amount,

              send_currency:
                state.context
                  .sendCurrency,

              receive_currency:
                recipient.currency,
            });

          if (!cancelled) {
            dispatch({
              type: "TRANSFER_CREATED",
              transfer,
              paymentMethod:
                defaultPaymentMethod,
            });
          }

          return;
        }

        if (!cancelled) {
          dispatch({
            type: "CLEAR_EFFECTS",
          });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (
          effect.type ===
          CHAT_EFFECT.PREVIEW_QUOTE
        ) {
          dispatch({
            type: "QUOTE_FAILED",
            error,
          });

          return;
        }

        if (
          effect.type ===
          CHAT_EFFECT.CREATE_TRANSFER
        ) {
          dispatch({
            type:
              "TRANSFER_CREATE_FAILED",
            message:
              safeApiErrorMessage(
                error,
                chatText("chat.transfer.create_error")
              ),
          });

          return;
        }

        dispatch({
          type: "CLEAR_EFFECTS",
        });
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [
    state.effects,
    state.context,
    navigate,
    isKycApproved,
    activeRecipient,
    defaultPaymentMethod,
    createTransferAsync,
  ]);

  return {
    messages: state.messages,
    quickReplies:
      state.quickReplies,
    flow: state.flow,
    step: state.step,
    context: state.context,
    activeCard: state.activeCard,

    profile,
    recipients,

    kycStatus,
    isKycApproved,

    defaultCurrency,

    transferDetail,
    fundingInstructions,
    fundingInstructionsNotReady,
    fundingInstructionsError,

    isLoading:
      Boolean(isLoadingProfile) ||
      Boolean(isLoadingRecipients) ||
      Boolean(isLoadingKyc),

    isLoadingRecipients,
    isLoadingPaymentMethods,
    isLoadingDetail,
    isLoadingFundingInstructions,

    isCreatingRecipient,
    isCreatingTransfer,
    isConfirmingTransfer,
    isPreparingTransfer,

    sendMessage,

    createRecipient,
    cancelRecipientCreation,

    confirmWithPin,

    bottomRef,

    historyHasMore: Boolean(
      state.historyHasMore
    ),
    firstName: resolvedFirstName,
    dispatch,
  };
}